/**
 * Nature scatter — small ground details children can discover.
 *
 * Unlike upright props or painted ground decals, scatter:
 * - is very small and sits on the grass
 * - paints above ground decals, below vertical scenery / character
 * - is always walkable (no collision)
 * - may optionally be a focus point of interest
 *
 * Future assets (acorns, sticks, shells, flowers, leaves, pebbles,
 * mushrooms, …) reuse this placer without renderer changes.
 */

import type {
  InteractiveObject,
  SpawnPoint,
  WorldSceneryLayer,
} from '@/world-prototype/types';
import type { LayoutProfilePair } from '@/world-prototype/camera/backyardPresentation';
import {
  placeActivationZone,
  type ActivationZoneDef,
} from '@/world-prototype/objects/activationZone';
import {
  localOffsetToWorld,
  normalizeRotationDeg,
} from '@/world-prototype/objects/propTransform';
import {
  spriteHeightFrac,
  type PlaceObjectOptions,
  type WorldObjectSprite,
} from '@/world-prototype/objects/placeObject';
import type { SpriteFrameRect } from '@/world-prototype/objects/spriteFrame';

/**
 * Paint depth band for nature scatter.
 * Above ground decals (~25–80), below character / upright props (~100–200).
 */
export function natureScatterDepthFromY(y: number): number {
  return Math.round(70 + y * 25);
}

/** One reusable small nature detail (pinecones, acorns, shells, …). */
export interface NatureScatterDef {
  id: string;
  title: string;
  icon: string;
  actionLabel: string;
  /** Center of the scatter item in world coords (0–1). */
  x: number;
  y: number;
  /** Rendered width as a fraction of world width (typically very small). */
  widthFrac: number;
  /** Per-presentation visual transforms (desktop / mobile). */
  layouts?: LayoutProfilePair;
  sprite: WorldObjectSprite;
  /**
   * Optional crop into `sprite.src` so many instances share one atlas file.
   * Pixel rect in the source texture.
   */
  spriteFrame?: SpriteFrameRect;
  /**
   * Rotation in degrees around the center.
   * Positive = clockwise (CSS). Default 0.
   */
  rotationDeg?: number;
  /** Horizontal mirror around the center. Omitted / false = as authored. */
  flipX?: boolean;
  /** Override the Y-derived paint depth. */
  depth?: number;
  /** Toggle without deleting the definition. */
  enabled?: boolean;
  /**
   * When false, no click hotspot / object-focus wiring.
   * Defaults to true when hitRadius > 0.
   */
  interactive?: boolean;
  /** Stand point offset from center (default just in front). */
  stand?: SpawnPoint;
  /** Hit center offset from center (defaults to center). */
  hit?: SpawnPoint;
  /** Clickable radius in world-width units. 0 / omitted = no interaction. */
  hitRadius?: number;
  /**
   * Small walk-in activation zone as offsets from the scatter center.
   * Independent of the full scatter PNG bounds.
   */
  activationZone?: ActivationZoneDef;
}

export interface PlacedNatureScatter {
  sceneryLayers: readonly WorldSceneryLayer[];
  interactiveObject: InteractiveObject | null;
}

function scatterDisplaySize(
  def: NatureScatterDef,
  worldAspect: number,
): { width: number; height: number } {
  const frame = def.spriteFrame;
  if (frame && frame.width > 0 && frame.height > 0) {
    return {
      width: def.widthFrac,
      height: def.widthFrac * (frame.height / frame.width) * worldAspect,
    };
  }
  return {
    width: def.widthFrac,
    height: spriteHeightFrac(def.widthFrac, def.sprite, worldAspect),
  };
}

/**
 * Expand a NatureScatterDef into scenery + optional focus hotspot.
 */
export function placeNatureScatter(
  def: NatureScatterDef,
  options: PlaceObjectOptions,
): PlacedNatureScatter {
  if (def.enabled === false) {
    return { sceneryLayers: [], interactiveObject: null };
  }

  const depth = def.depth ?? natureScatterDepthFromY(def.y);
  const rotationDeg = normalizeRotationDeg(def.rotationDeg ?? 0);
  const flipX = def.flipX === true;
  const { width, height } = scatterDisplaySize(def, options.worldAspect);
  const hitRadius = def.hitRadius ?? 0;

  const layer: WorldSceneryLayer = {
    id: def.id,
    kind: 'nature-scatter',
    zIndex: depth,
    depth,
    src: def.sprite.src,
    alt: def.sprite.alt,
    x: def.x,
    y: def.y,
    width,
    height,
    rotationDeg: rotationDeg || undefined,
    ...(flipX ? { flipX: true } : {}),
    ...(def.spriteFrame
      ? {
          spriteFrame: {
            x: def.spriteFrame.x,
            y: def.spriteFrame.y,
            width: def.spriteFrame.width,
            height: def.spriteFrame.height,
            imageWidth: def.sprite.imageWidth,
            imageHeight: def.sprite.imageHeight,
          },
        }
      : {}),
    placeholderClass: `wp-layer-${def.id}`,
  };

  const wantInteractive = def.interactive !== false && hitRadius > 0;
  const hitLocal = localOffsetToWorld(
    def.hit ?? { x: 0, y: 0 },
    rotationDeg,
    options.worldAspect,
    flipX,
  );
  const standLocal = localOffsetToWorld(
    def.stand ?? { x: 0, y: 0.025 },
    rotationDeg,
    options.worldAspect,
    flipX,
  );

  const zoneLocal = def.activationZone
    ? localOffsetToWorld(
        { x: def.activationZone.x, y: def.activationZone.y },
        rotationDeg,
        options.worldAspect,
        flipX,
      )
    : { x: 0, y: 0 };

  const interactiveObject = wantInteractive
    ? ({
        id: def.id,
        title: def.title,
        icon: def.icon,
        actionLabel: def.actionLabel,
        sceneryId: def.id,
        x: def.x + hitLocal.x,
        y: def.y + hitLocal.y,
        standX: def.x + standLocal.x,
        standY: def.y + standLocal.y,
        hitRadius,
        activationZone: placeActivationZone(
          def.activationZone,
          { x: def.x, y: def.y },
          zoneLocal,
        ),
        shape: 'scatter',
      } satisfies InteractiveObject)
    : null;

  return {
    sceneryLayers: [layer],
    interactiveObject,
  };
}

/** Place many nature-scatter defs for easy space wiring. */
export function placeNatureScatters(
  defs: readonly NatureScatterDef[],
  options: PlaceObjectOptions,
): {
  sceneryLayers: WorldSceneryLayer[];
  interactiveObjects: InteractiveObject[];
} {
  const sceneryLayers: WorldSceneryLayer[] = [];
  const interactiveObjects: InteractiveObject[] = [];
  for (const def of defs) {
    const placed = placeNatureScatter(def, options);
    sceneryLayers.push(...placed.sceneryLayers);
    if (placed.interactiveObject) {
      interactiveObjects.push(placed.interactiveObject);
    }
  }
  return { sceneryLayers, interactiveObjects };
}
